import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertCircle, Trash2, ZoomIn, ZoomOut, RotateCcw, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { seatingApi } from '@/lib/api/seating.api';
import type { SeatingVisualizationPayload, SeatSlot } from '@/types/seating-v2.types';
import type { Guest } from '@/types/guest.types';

// Arrangements
import { GridArrangement } from './arrangements/GridArrangement';
import { RowsArrangement } from './arrangements/RowsArrangement';
import { SingleArrangement } from './arrangements/SingleArrangement';
import { UShapeArrangement } from './arrangements/UShapeArrangement';

interface SeatingBoardProps {
  eventId: number;
  guests?: Guest[];
  onAssignmentChange?: () => void;
  /** When true, hides the guest sidebar and disables all assignment interactions */
  readOnly?: boolean;
  /** URL to navigate to for seat assignment (shown in the read-only banner) */
  assignmentUrl?: string;
}

/**
 * SeatingBoard — Main orchestrator for the layered seating visualization.
 * Fetches data from /visualization-v2, picks the right arrangement component,
 * and handles drag-and-drop guest assignment.
 */
export const SeatingBoard: React.FC<SeatingBoardProps> = ({
  eventId,
  guests = [],
  onAssignmentChange,
  readOnly = false,
  assignmentUrl,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const [data, setData] = useState<SeatingVisualizationPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);
  const [searchGuest, setSearchGuest] = useState('');
  const [zoom, setZoom] = useState(1);
  const [guestPanelOpen, setGuestPanelOpen] = useState(true);

  // ── Load visualization data ──
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await seatingApi.getVisualizationV2(eventId);
      if (response.success) {
        setData(response.data);
      } else {
        setError(t('events:seating.errors.loadData'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('events:seating.errors.loadVisualization'));
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Assign guest to seat ──
  const handleSeatDrop = useCallback(async (globalSeatNumber: number, guestId: number) => {
    if (!data) return;
    // Find the positionId for this globalSeatNumber
    let positionId: number | undefined;
    for (const unit of data.units) {
      const seat = unit.seats.find(s => s.globalSeatNumber === globalSeatNumber);
      if (seat?.positionId) {
        positionId = seat.positionId;
        break;
      }
    }
    if (!positionId) {
      setError(t('events:seating.errors.seatPositionNotFound'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await seatingApi.assignGuest(eventId, guestId, positionId);
      if (response.success) {
        setSuccess(t('events:seating.messages.guestAssignedSuccess'));
        await loadData();
        onAssignmentChange?.();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.error || t('events:seating.errors.assignGuest'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('events:seating.errors.assignGuest'));
    } finally {
      setLoading(false);
    }
  }, [data, eventId, loadData, onAssignmentChange, t]);

  // ── Also support clicking a seat then clicking a guest ──
  const handleSeatClick = useCallback((globalSeatNumber: number) => {
    setSelectedSeat(prev => prev === globalSeatNumber ? null : globalSeatNumber);
  }, []);

  const handleAssignViaClick = useCallback(async (guestId: number) => {
    if (selectedSeat === null) return;
    await handleSeatDrop(selectedSeat, guestId);
    setSelectedSeat(null);
  }, [selectedSeat, handleSeatDrop]);

  // ── Remove assignment ──
  const handleRemoveAssignment = useCallback(async (guestId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await seatingApi.removeAssignment(eventId, guestId);
      if (response.success) {
        setSuccess(t('events:seating.messages.assignmentRemoved'));
        await loadData();
        onAssignmentChange?.();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(t('events:seating.errors.removeAssignment'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('events:seating.errors.removeAssignment'));
    } finally {
      setLoading(false);
    }
  }, [eventId, loadData, onAssignmentChange, t]);

  // When readOnly, the guest panel is always hidden
  const effectivePanelOpen = readOnly ? false : guestPanelOpen;

  // ── Get currently selected seat info ──
  const getSelectedSeatInfo = (): { seat: SeatSlot; unitLabel: string } | null => {
    if (!data || selectedSeat === null) return null;
    for (const unit of data.units) {
      const seat = unit.seats.find(s => s.globalSeatNumber === selectedSeat);
      if (seat) return { seat, unitLabel: unit.label };
    }
    return null;
  };

  // ── Unassigned guests list ──
  const assignedGuestIds = new Set<number>();
  if (data) {
    for (const unit of data.units) {
      for (const seat of unit.seats) {
        if (seat.assignedGuestId) assignedGuestIds.add(seat.assignedGuestId);
      }
    }
  }

  const unassignedGuests = guests
    .filter(g => !assignedGuestIds.has(g.id))
    .filter(g =>
      searchGuest === '' ||
      `${g.name || ''}`.toLowerCase().includes(searchGuest.toLowerCase()) ||
      (g.email && g.email.toLowerCase().includes(searchGuest.toLowerCase()))
    );

  // ── Assigned guests flat list (for sidebar) ──
  const assignedSeats: Array<SeatSlot & { unitLabel: string }> = [];
  if (data) {
    for (const unit of data.units) {
      for (const seat of unit.seats) {
        if (seat.assignedGuestId) {
          assignedSeats.push({ ...seat, unitLabel: unit.label });
        }
      }
    }
  }

  // ── Loading state ──
  if (loading && !data) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.hasGeneratedLayout) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('events:seating.layout.title')}</CardTitle>
          <CardDescription>{t('events:seating.layout.noGenerated')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>{t('events:seating.layout.generateFirst')}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { layoutConfig, units } = data;
  const { unit, arrangement } = layoutConfig;

  return (
    <div className="space-y-4">
      {/* Read-only banner */}
      {readOnly && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center justify-between">
          <span>{t('events:seating.readOnly.banner')}</span>
          {assignmentUrl && (
            <a
              href={assignmentUrl}
              className="text-blue-800 font-semibold hover:underline ml-4 flex items-center gap-1 whitespace-nowrap"
            >
              {t('events:seating.readOnly.assignSeats')}
            </a>
          )}
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-green-800">{success}</div>
        </div>
      )}

      <div className={`grid grid-cols-1 ${effectivePanelOpen ? 'lg:grid-cols-4' : ''} gap-4`}>
        {/* ── Canvas Area ── */}
        <div className={guestPanelOpen ? 'lg:col-span-3' : ''}>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('events:seating.layout.title')}</CardTitle>
                  <CardDescription>
                    {t('events:seating.layout.summary', {
                      totalSeats: data.totalSeats,
                      assignedCount: data.assignedCount,
                      configType: data.configType.replace('_', ' '),
                    })}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.min(z + 0.15, 2))} title={t('events:seating.actions.zoomIn')}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.max(z - 0.15, 0.4))} title={t('events:seating.actions.zoomOut')}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setZoom(1)} title={t('events:seating.actions.resetZoom')}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  {!readOnly && (
                    <Button size="sm" variant="outline" onClick={() => setGuestPanelOpen(p => !p)} title={guestPanelOpen ? t('events:seating.actions.hideGuestsPanel') : t('events:seating.actions.showGuestsPanel')}>
                      {guestPanelOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div
                className="border rounded-lg overflow-auto relative"
                style={{ height: '600px', background: '#f8fafc' }}
              >
                <div
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                    minWidth: 'max-content',
                    minHeight: 'max-content',
                  }}
                >
                  {/* Pick the right arrangement */}
                  {arrangement.type === 'grid' && (
                    <GridArrangement
                      unit={unit}
                      units={units}
                      cols={arrangement.cols}
                      spacing={arrangement.spacing}
                      selectedSeat={readOnly ? null : selectedSeat}
                      onSeatClick={readOnly ? () => {} : handleSeatClick}
                      onSeatDrop={readOnly ? () => {} : handleSeatDrop}
                    />
                  )}
                  {arrangement.type === 'rows' && (
                    <RowsArrangement
                      unit={unit}
                      units={units}
                      cols={arrangement.cols || 10}
                      spacing={arrangement.spacing}
                      aisles={arrangement.aisles}
                      stagger={arrangement.stagger}
                      selectedSeat={readOnly ? null : selectedSeat}
                      onSeatClick={readOnly ? () => {} : handleSeatClick}
                      onSeatDrop={readOnly ? () => {} : handleSeatDrop}
                    />
                  )}
                  {arrangement.type === 'single' && (
                    <SingleArrangement
                      unit={unit}
                      units={units}
                      selectedSeat={readOnly ? null : selectedSeat}
                      onSeatClick={readOnly ? () => {} : handleSeatClick}
                      onSeatDrop={readOnly ? () => {} : handleSeatDrop}
                    />
                  )}
                  {(arrangement.type === 'u-shape') && (
                    <UShapeArrangement
                      unit={unit}
                      units={units}
                      spacing={arrangement.spacing}
                      openSide={arrangement.openSide}
                      selectedSeat={readOnly ? null : selectedSeat}
                      onSeatClick={readOnly ? () => {} : handleSeatClick}
                      onSeatDrop={readOnly ? () => {} : handleSeatDrop}
                    />
                  )}
                  {arrangement.type === 'scatter' && (
                    <GridArrangement
                      unit={unit}
                      units={units}
                      cols={arrangement.cols || Math.ceil(Math.sqrt(units.length))}
                      spacing={arrangement.spacing}
                      selectedSeat={readOnly ? null : selectedSeat}
                      onSeatClick={readOnly ? () => {} : handleSeatClick}
                      onSeatDrop={readOnly ? () => {} : handleSeatDrop}
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected seat details — only in interactive mode */}
          {!readOnly && (() => {
            const info = getSelectedSeatInfo();
            if (!info) return null;
            return (
              <Card className="mt-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {info.unitLabel} — {info.seat.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {info.seat.assignedGuestId ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{info.seat.assignedGuestName}</p>
                        <p className="text-sm text-muted-foreground">{info.seat.assignedGuestEmail}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRemoveAssignment(info.seat.assignedGuestId!)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('common:actions.remove')}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t('events:seating.selected.noGuestAssigned')}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* ── Guest Sidebar ── */}
        {effectivePanelOpen && <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('events:seating.sidebar.unassignedTitle')}</CardTitle>
              <CardDescription>{t('events:seating.sidebar.remaining', { count: unassignedGuests.length })}</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                placeholder={t('events:seating.sidebar.searchPlaceholder')}
                value={searchGuest}
                onChange={(e) => setSearchGuest(e.target.value)}
                className="mb-4"
                size={1}
              />
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {unassignedGuests.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {searchGuest ? t('events:seating.sidebar.noMatchingGuests') : t('events:seating.sidebar.allAssigned')}
                  </p>
                ) : (
                  unassignedGuests.map(guest => (
                    <div
                      key={guest.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('guestId', String(guest.id));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        // Accept seat drops
                        if (e.dataTransfer.types.includes('seatnumber')) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const seatNum = parseInt(e.dataTransfer.getData('seatNumber'), 10);
                        if (!isNaN(seatNum)) {
                          handleSeatDrop(seatNum, guest.id);
                        }
                      }}
                      onClick={() => handleAssignViaClick(guest.id)}
                      className={`
                        p-2 border rounded cursor-move hover:bg-slate-50 text-sm
                        transition-colors
                        ${selectedSeat !== null ? 'hover:bg-blue-50 hover:border-blue-300 cursor-pointer' : ''}
                      `}
                    >
                      <p className="font-medium">{guest.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{guest.email}</p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Assigned Guests Summary */}
          {assignedSeats.length > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base">{t('events:seating.sidebar.assignedTitle', { count: assignedSeats.length })}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto text-xs">
                  {assignedSeats.map(seat => (
                    <div key={seat.globalSeatNumber} className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{seat.assignedGuestName}</p>
                        <p className="text-muted-foreground">{seat.unitLabel} · {seat.label}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => seat.assignedGuestId && handleRemoveAssignment(seat.assignedGuestId)}
                        className="h-6 w-6 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>}
      </div>
    </div>
  );
};

export default SeatingBoard;
